import React, { useEffect, useMemo, useRef, useState } from "react";
import Plot from "react-plotly.js";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { DriftService, DriftCheck } from "../../../services/DriftService";
import { usePermissions } from "../../../PermissionsProvider";
import { useTheme } from "../../../contexts/ThemeContext";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import {
  Loader2Icon,
  FingerprintIcon,
  HashIcon,
  FlaskConicalIcon,
  ActivityIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  TrendingUpIcon,
} from "lucide-react";

enum ProfileStatus {
  PENDING = "PENDING",
  GENERATING = "GENERATING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

interface ProjectionPoint {
  x: number;
  y: number;
  sample_index: number;
  source: string;
  dataset: string;
}

interface StatsProfile {
  message_count: number;
  avg_tokens: number;
  vocab_richness: number;
}

interface ModelProfileData {
  version: string;
  finetune_run_id: number;
  dataset_composition_id: number | null;
  status: ProfileStatus;
  embedding_model: string;
  embedding_dim: number;
  message_count: number;
  test_message_count: number;
  global_centroid: number[];
  entropy: number;
  stats: StatsProfile;
  projection_2d: ProjectionPoint[];
  created_at: string | null;
  error: string | null;
}

const DATASET_PALETTE = [
  "#22c55e",
  "#3b82f6",
  "#8b5cf6",
  "#06b6d4",
  "#ec4899",
  "#14b8a6",
  "#6366f1",
  "#84cc16",
  "#a855f7",
  "#0ea5e9",
];
const TEST_COLOR = "#f59e0b";

interface ModelProfileProps {
  finetuneRunId: number;
  modelId?: number;
}

function percentile(arr: number[], p: number): number {
  const sorted = [...arr].sort((a, b) => a - b);
  const i = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(i);
  const hi = Math.ceil(i);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (i - lo);
}

const POLL_INTERVAL_MS = 5000;

const ModelProfile: React.FC<ModelProfileProps> = ({ finetuneRunId, modelId }) => {
  const { apiKey } = usePermissions();
  const [profile, setProfile] = useState<ModelProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [driftChecks, setDriftChecks] = useState<DriftCheck[]>([]);
  const [driftLoading, setDriftLoading] = useState(false);
  const [triggeringDrift, setTriggeringDrift] = useState(false);

  const loadProfile = React.useCallback(() => {
    setIsLoading((prev) => !profile && prev === false ? false : true);
    setNotFound(false);
    FinetuneRunService.getModelProfile(
      finetuneRunId,
      undefined,
      apiKey
    ).subscribe(
      (data: any) => {
        if (data.status === "GENERATING" && !data.version) {
          setIsGenerating(true);
          setNotFound(true);
          setIsLoading(false);
        } else {
          setProfile(data as ModelProfileData);
          setIsGenerating(false);
          setIsLoading(false);
        }
      },
      (error: any) => {
        if (error?.status === 404 || error?.response?.status === 404) {
          setNotFound(true);
        }
        setIsLoading(false);
      }
    );
  }, [finetuneRunId, apiKey]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (!isGenerating) return;
    const timer = setInterval(() => loadProfile(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isGenerating, loadProfile]);

  useEffect(() => {
    if (!modelId || !profile) return;
    setDriftLoading(true);
    const sub = DriftService.getDriftHistory(modelId, undefined, apiKey, 10).subscribe(
      (data: DriftCheck[]) => {
        setDriftChecks(data);
        setDriftLoading(false);
      },
      () => setDriftLoading(false)
    );
    return () => sub.unsubscribe();
  }, [modelId, apiKey, profile]);

  const driftSubRef = useRef<{ unsubscribe: () => void } | null>(null);

  useEffect(() => {
    return () => { driftSubRef.current?.unsubscribe(); };
  }, []);

  const handleTriggerDrift = () => {
    if (!modelId) return;
    setTriggeringDrift(true);
    driftSubRef.current?.unsubscribe();
    driftSubRef.current = DriftService.triggerDriftCheck(modelId, undefined, apiKey).subscribe(
      (check: DriftCheck) => {
        setDriftChecks((prev) => [check, ...prev]);
        setTriggeringDrift(false);
      },
      () => setTriggeringDrift(false)
    );
  };

  const handleGenerate = () => {
    setIsGenerating(true);
    FinetuneRunService.generateModelProfile(
      finetuneRunId,
      undefined,
      apiKey
    ).subscribe(
      () => {},
      (error: any) => {
        console.error("Failed to generate profile:", error);
        setIsGenerating(false);
      }
    );
  };

  if (isLoading && !isGenerating) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2Icon className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isGenerating && !profile) {
    return (
      <div className="space-y-6">
        <Card className="border-border">
          <CardContent className="p-8 text-center space-y-4">
            <Loader2Icon className="w-12 h-12 mx-auto text-muted-foreground animate-spin" />
            <h3 className="text-lg font-gc-med text-foreground">
              Generating Model Profile
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Embedding your training and test data to build the model profile.
              This may take a few minutes.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="space-y-6">
        <Card className="border-border">
          <CardContent className="p-8 text-center space-y-4">
            <FingerprintIcon className="w-12 h-12 mx-auto text-muted-foreground" />
            <h3 className="text-lg font-gc-med text-foreground">
              No Model Profile Yet
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Generate a model profile from your training data to understand its
              distribution and monitor for drift in production.
            </p>
            <Button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="gap-2">
              <FingerprintIcon className="w-4 h-4" />
              Generate Profile
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasTestData = profile.test_message_count > 0;

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <HashIcon className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground font-gc-reg">
                Train Messages
              </span>
            </div>
            <p className="text-lg font-gc-med text-foreground">
              {profile.message_count.toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <FlaskConicalIcon className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground font-gc-reg">
                Test Messages
              </span>
            </div>
            <p className="text-lg font-gc-med text-foreground">
              {hasTestData
                ? profile.test_message_count.toLocaleString()
                : "—"}
            </p>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <ActivityIcon className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground font-gc-reg">
                Entropy (bits)
              </span>
            </div>
            <p className="text-lg font-gc-med text-foreground">
              {(profile.entropy ?? 0).toFixed(2)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Drift Status */}
      {modelId && (
        <DriftStatusSection
          driftChecks={driftChecks}
          loading={driftLoading}
          triggering={triggeringDrift}
          onTrigger={handleTriggerDrift}
        />
      )}

      {/* Single hero visualization */}
      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="text-sm font-gc-med text-foreground mb-4">
            Model Profile
          </h3>
          <ProfilePlot points={profile.projection_2d} />
        </CardContent>
      </Card>
    </div>
  );
};

const DriftStatusSection: React.FC<{
  driftChecks: DriftCheck[];
  loading: boolean;
  triggering: boolean;
  onTrigger: () => void;
}> = ({ driftChecks, loading, triggering, onTrigger }) => {
  const latestCheck = driftChecks.length > 0 ? driftChecks[0] : null;

  return (
    <Card className="border-border">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUpIcon className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-sm font-gc-med text-foreground">
              Data Drift Status
            </h3>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onTrigger}
            disabled={triggering}
            className="gap-2"
          >
            {triggering ? (
              <Loader2Icon className="w-3 h-3 animate-spin" />
            ) : (
              <TrendingUpIcon className="w-3 h-3" />
            )}
            Run Drift Check
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-20">
            <Loader2Icon className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : !latestCheck ? (
          <p className="text-sm text-muted-foreground">
            No drift checks have been run yet. Click "Run Drift Check" to
            analyze production data against this model's training profile.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              {latestCheck.drift_detected ? (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-100 dark:bg-amber-900/20">
                  <AlertTriangleIcon className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  <span className="text-sm font-gc-med text-amber-700 dark:text-amber-400">
                    Drift Detected — Needs Training
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-green-100 dark:bg-green-900/20">
                  <CheckCircleIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="text-sm font-gc-med text-green-700 dark:text-green-400">
                    Stable — No Drift
                  </span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <span className="text-xs text-muted-foreground block">PSI</span>
                <span className="text-sm font-gc-med text-foreground">
                  {latestCheck.psi != null ? latestCheck.psi.toFixed(4) : "—"}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">
                  Wasserstein
                </span>
                <span className="text-sm font-gc-med text-foreground">
                  {latestCheck.wasserstein != null
                    ? latestCheck.wasserstein.toFixed(4)
                    : "—"}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">
                  Novel Points
                </span>
                <span className="text-sm font-gc-med text-foreground">
                  {latestCheck.novel_count.toLocaleString()}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">
                  Requests Checked
                </span>
                <span className="text-sm font-gc-med text-foreground">
                  {latestCheck.request_count.toLocaleString()}
                </span>
              </div>
            </div>

            {driftChecks.length > 1 && (
              <div className="mt-3">
                <h4 className="text-xs font-gc-med text-muted-foreground mb-2">
                  Recent Checks
                </h4>
                <div className="space-y-1.5">
                  {driftChecks.slice(0, 5).map((check) => (
                    <div
                      key={check.id}
                      className="flex items-center justify-between text-xs py-1.5 px-2 rounded bg-muted/50"
                    >
                      <div className="flex items-center gap-2">
                        {check.drift_detected ? (
                          <AlertTriangleIcon className="w-3 h-3 text-amber-500" />
                        ) : (
                          <CheckCircleIcon className="w-3 h-3 text-green-500" />
                        )}
                        <span className="text-muted-foreground">
                          PSI: {check.psi?.toFixed(3) ?? "—"} | Novel:{" "}
                          {check.novel_count}
                        </span>
                      </div>
                      <span className="text-muted-foreground">
                        {check.date_created
                          ? new Date(check.date_created).toLocaleDateString()
                          : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PERCENTILE_BANDS = [
  { p: 50, label: "p50", dash: "dot", color: "56, 189, 248", lineWidth: 1.5 },
  { p: 75, label: "p75", dash: "dashdot", color: "168, 85, 247", lineWidth: 1.5 },
  { p: 95, label: "p95", dash: "dash", color: "251, 146, 60", lineWidth: 1.5 },
] as const;

const ProfilePlot: React.FC<{ points: ProjectionPoint[] }> = ({
  points,
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const {
    traces,
    shapes,
    annotations,
    legendItems,
  } = useMemo(() => buildPlotData(points), [points]);

  if (points.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">
        No projection data
      </div>
    );
  }

  const gridColor = "rgba(128,128,128,0.15)";
  const zeroColor = "rgba(128,128,128,0.2)";
  const fontColor = isDark ? "#d1d5db" : "#374151";

  const swatchSvg = (item: LegendItem) => {
    if (item.symbol === "cross") {
      return (
        <svg width="14" height="14" viewBox="0 0 14 14">
          <line x1="7" y1="1" x2="7" y2="13" stroke={item.color} strokeWidth="2.5" />
          <line x1="1" y1="7" x2="13" y2="7" stroke={item.color} strokeWidth="2.5" />
        </svg>
      );
    }
    if (item.symbol === "diamond") {
      return (
        <svg width="12" height="12" viewBox="0 0 12 12">
          <polygon points="6,1 11,6 6,11 1,6" fill={item.color} />
        </svg>
      );
    }
    return (
      <svg width="10" height="10" viewBox="0 0 10 10">
        <circle cx="5" cy="5" r="4" fill={item.color} />
      </svg>
    );
  };

  return (
    <div>
      <Plot
        data={traces}
        layout={{
          autosize: true,
          height: 560,
          margin: { l: 40, r: 10, t: 10, b: 40 },
          paper_bgcolor: "transparent",
          plot_bgcolor: "transparent",
          barmode: "overlay",
          showlegend: false,
          shapes,
          annotations,
          xaxis: {
            domain: [0, 0.82],
            title: { text: "PC1", font: { color: fontColor } },
            gridcolor: gridColor,
            zerolinecolor: zeroColor,
            tickfont: { color: fontColor },
          },
          yaxis: {
            domain: [0, 0.82],
            title: { text: "PC2", font: { color: fontColor } },
            gridcolor: gridColor,
            zerolinecolor: zeroColor,
            tickfont: { color: fontColor },
          },
          xaxis2: {
            domain: [0.85, 1],
            showticklabels: false,
            showgrid: false,
            zeroline: false,
          },
          yaxis2: {
            domain: [0.85, 1],
            showticklabels: false,
            showgrid: false,
            zeroline: false,
          },
          font: { family: "inherit", size: 11, color: fontColor },
        }}
        config={{ displayModeBar: false, responsive: true }}
        style={{ width: "100%" }}
      />
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 px-4 pt-1 pb-2 text-xs text-muted-foreground">
        {legendItems.map((item) => (
          <span key={item.label} className="inline-flex items-center gap-1 whitespace-nowrap">
            {swatchSvg(item)}
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
};

interface LegendItem {
  label: string;
  color: string;
  symbol: "circle" | "diamond" | "cross";
}

function buildPlotData(points: ProjectionPoint[]) {
  const trainPts = points.filter((p) => p.source === "train");
  const testPts = points.filter((p) => p.source === "test");

  const centroidX =
    trainPts.reduce((s, p) => s + p.x, 0) / (trainPts.length || 1);
  const centroidY =
    trainPts.reduce((s, p) => s + p.y, 0) / (trainPts.length || 1);

  const trainDatasets = Array.from(
    new Set(trainPts.map((p) => p.dataset))
  );
  const testDatasets = Array.from(
    new Set(testPts.map((p) => p.dataset))
  );

  const colorForDataset = (ds: string, isTest: boolean): string => {
    if (isTest) {
      const tidx = testDatasets.indexOf(ds);
      if (testDatasets.length > 1) {
        return DATASET_PALETTE[
          (trainDatasets.length + tidx) % DATASET_PALETTE.length
        ];
      }
      return TEST_COLOR;
    }
    return DATASET_PALETTE[trainDatasets.indexOf(ds) % DATASET_PALETTE.length];
  };

  const traces: any[] = [];
  const legendItems: LegendItem[] = [];

  const addDatasetTraces = (
    ds: string,
    isTest: boolean,
    pts: ProjectionPoint[]
  ) => {
    const color = colorForDataset(ds, isTest);
    const label = isTest ? `Test: ${ds}` : ds;
    const symbol = isTest ? "diamond" : "circle";

    legendItems.push({ label, color, symbol });

    traces.push({
      x: pts.map((p) => p.x),
      y: pts.map((p) => p.y),
      mode: "markers",
      type: "scatter",
      name: label,
      showlegend: false,
      marker: { color, size: isTest ? 6 : 4, opacity: isTest ? 0.8 : 0.5, symbol },
      xaxis: "x",
      yaxis: "y",
    });

    traces.push({
      x: pts.map((p) => p.x),
      type: "histogram",
      showlegend: false,
      marker: { color, opacity: 0.35 },
      xaxis: "x",
      yaxis: "y2",
    });

    traces.push({
      y: pts.map((p) => p.y),
      type: "histogram",
      showlegend: false,
      marker: { color, opacity: 0.35 },
      xaxis: "x2",
      yaxis: "y",
    });
  };

  trainDatasets.forEach((ds) =>
    addDatasetTraces(ds, false, trainPts.filter((p) => p.dataset === ds))
  );
  testDatasets.forEach((ds) =>
    addDatasetTraces(ds, true, testPts.filter((p) => p.dataset === ds))
  );

  legendItems.push({ label: "Centroid", color: "#ff3232", symbol: "cross" });

  traces.push({
    x: [centroidX],
    y: [centroidY],
    mode: "markers",
    type: "scatter",
    name: "Centroid",
    showlegend: false,
    marker: {
      color: "#ff3232",
      size: 18,
      symbol: "cross-thin",
      line: { color: "#ffffff", width: 3 },
    },
    xaxis: "x",
    yaxis: "y",
  });

  const shapes: any[] = [];
  const annotations: any[] = [];
  const trainX = trainPts.map((p) => p.x);
  const trainY = trainPts.map((p) => p.y);

  if (trainX.length > 10) {
    PERCENTILE_BANDS.forEach(({ p, label, dash, color, lineWidth }) => {
      const loP = (100 - p) / 2;
      const hiP = (100 + p) / 2;
      const x0 = percentile(trainX, loP);
      const x1 = percentile(trainX, hiP);
      const y0 = percentile(trainY, loP);
      const y1 = percentile(trainY, hiP);

      const lineColor = `rgba(${color}, 0.7)`;
      const fillColor = `rgba(${color}, 0.06)`;
      const labelColor = `rgba(${color}, 0.9)`;
      const marginalColor = `rgba(${color}, 0.5)`;
      const marginalFill = `rgba(${color}, 0.08)`;

      shapes.push({
        type: "rect",
        xref: "x",
        yref: "y",
        x0,
        y0,
        x1,
        y1,
        line: { color: lineColor, width: lineWidth, dash },
        fillcolor: fillColor,
      });

      annotations.push({
        x: x1,
        y: y1,
        xref: "x",
        yref: "y",
        text: label,
        showarrow: false,
        font: { size: 9, color: labelColor },
        xanchor: "left",
        yanchor: "bottom",
      });

      // Top marginal: vertical band between x percentile boundaries
      shapes.push({
        type: "rect",
        xref: "x",
        yref: "paper",
        x0,
        x1,
        y0: 0.85,
        y1: 1.0,
        line: { width: 0 },
        fillcolor: marginalFill,
      });
      shapes.push(
        {
          type: "line",
          xref: "x",
          yref: "paper",
          x0,
          x1: x0,
          y0: 0.85,
          y1: 1.0,
          line: { color: marginalColor, width: 1, dash },
        },
        {
          type: "line",
          xref: "x",
          yref: "paper",
          x0: x1,
          x1,
          y0: 0.85,
          y1: 1.0,
          line: { color: marginalColor, width: 1, dash },
        }
      );

      // Right marginal: horizontal band between y percentile boundaries
      shapes.push({
        type: "rect",
        xref: "paper",
        yref: "y",
        x0: 0.85,
        x1: 1.0,
        y0,
        y1,
        line: { width: 0 },
        fillcolor: marginalFill,
      });
      shapes.push(
        {
          type: "line",
          xref: "paper",
          yref: "y",
          x0: 0.85,
          x1: 1.0,
          y0,
          y1: y0,
          line: { color: marginalColor, width: 1, dash },
        },
        {
          type: "line",
          xref: "paper",
          yref: "y",
          x0: 0.85,
          x1: 1.0,
          y0: y1,
          y1,
          line: { color: marginalColor, width: 1, dash },
        }
      );
    });
  }

  return { traces, shapes, annotations, legendItems };
}

export default ModelProfile;
